import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { billingApi, formatPrice, MembershipPlan } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useMembership } from '../hooks/useMembership';
import './Join.css';

const PERKS: { key: keyof Pick<MembershipPlan, 'includesLibrary' | 'includesFeedback' | 'includesLive'>; label: string }[] = [
  { key: 'includesLibrary', label: 'Full move and pattern library' },
  { key: 'includesFeedback', label: 'Send videos for instructor feedback' },
  { key: 'includesLive', label: 'Live classes and replays' },
];

type ApiError = { response?: { data?: { error?: string } } };

/** The studio's membership plans, with Stripe Checkout behind each Join button. */
const JoinPage: React.FC = () => {
  const { user } = useAuth();
  const tenant = useTenant();
  const { membership } = useMembership();
  const [joining, setJoining] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['billing', 'plans'], queryFn: billingApi.getPlans });

  useEffect(() => {
    if (tenant?.name) document.title = `Join ${tenant.name}`;
  }, [tenant?.name]);

  const paidSub = membership?.subscriptions.find((s) => s.source === 'stripe');

  const join = async (plan: MembershipPlan) => {
    setError(null);
    setJoining(plan.id);
    try {
      const { url } = await billingApi.checkout(plan.id);
      window.location.href = url;
    } catch (err) {
      const msg = (err as ApiError)?.response?.data?.error;
      setError(
        msg === 'already_subscribed'
          ? 'You already have a membership. Use “Manage membership” to switch plans.'
          : msg || 'Something went wrong. Please try again.',
      );
      setJoining(null);
    }
  };

  const openPortal = async () => {
    setError(null);
    try {
      const { url } = await billingApi.portal();
      window.location.href = url;
    } catch {
      setError('Couldn’t open billing. Please try again.');
    }
  };

  if (isLoading) return <div className="join-page"><p className="join-muted">Loading plans…</p></div>;

  if (!data?.membershipsEnabled || data.plans.length === 0) {
    return (
      <div className="join-page">
        <h1>Memberships</h1>
        <p className="join-muted">{tenant?.name || 'This studio'} isn’t offering online memberships right now.</p>
        <Link to="/" className="btn btn-secondary">Back to home</Link>
      </div>
    );
  }

  const currency = data.currency || 'usd';

  return (
    <div className="join-page">
      <header className="join-header">
        <span className="join-eyebrow">Membership</span>
        <h1>Train with {tenant?.name || 'us'} between classes</h1>
        <p className="join-lede">
          Practice every move with slow motion, mirroring and looping, wherever you are. Cancel any time.
        </p>
      </header>

      {membership?.isStaff && (
        <p className="join-notice">You’re part of the studio’s team, so you already have full access.</p>
      )}
      {paidSub && (
        <div className="join-notice">
          You’re on <strong>{paidSub.plan.name}</strong>.{' '}
          <button type="button" className="btn btn-secondary btn-small" onClick={openPortal}>Manage membership</button>
        </div>
      )}
      {!data.canJoin && (
        <p className="join-notice">Online sign-up opens soon. Ask the studio about joining in the meantime.</p>
      )}
      {error && <p className="join-error" role="alert">{error}</p>}

      <div className="join-plans">
        {data.plans.map((plan) => (
          <article key={plan.id} className="join-plan">
            <h2>{plan.name}</h2>
            <p className="join-price">
              <span className="join-amount">{formatPrice(plan.priceCents, currency)}</span>
              <span className="join-interval">/ {plan.interval}</span>
            </p>
            {plan.description && <p className="join-muted">{plan.description}</p>}
            <ul className="join-perks">
              <li>Free moves and patterns</li>
              {PERKS.filter((p) => plan[p.key]).map((p) => (
                <li key={p.key}>{p.label}</li>
              ))}
            </ul>
            {!user ? (
              <Link to="/signup?returnUrl=/join" className="btn btn-primary join-cta">Sign up to join</Link>
            ) : (
              <button
                type="button"
                className="btn btn-primary join-cta"
                onClick={() => join(plan)}
                disabled={!data.canJoin || joining !== null || !!paidSub || !!membership?.isStaff}
              >
                {joining === plan.id ? 'Opening checkout…' : 'Join'}
              </button>
            )}
          </article>
        ))}
      </div>

      {!user && (
        <p className="join-muted join-login">
          Already have an account? <Link to="/login?returnUrl=/join">Log in</Link>
        </p>
      )}
      <p className="join-muted join-fineprint">
        Payments are processed securely by Stripe for {tenant?.name || 'the studio'}. Memberships renew automatically
        until you cancel.
      </p>
    </div>
  );
};

export default JoinPage;
